<template>
  <div
    v-on-clickaway="closeFromOutside"
    class="relative mb-4 woot-select"
    @keydown.esc="onEscape"
  >
    <!--
      ── THE PHONE BRANCH ──────────────────────────────────────────────────────────────────
      A real `<select>`, deliberately, below `sm`.

      This is the honest answer rather than a shortcut. At 390px the custom listbox loses on
      three counts at once, and none of them is a styling problem:

        · The filter field is the whole point of this component, and on a phone typing into it
          raises the software keyboard over roughly half the viewport — so the popup that the
          field is filtering gets pushed into the remaining ~200px, which fits three rows.
        · An absolutely-positioned popup is bounded by the nearest scroll container. On a
          narrow screen that container is usually the page itself and the popup ends up
          straddling the fold; the OS picker is a sheet OVER the page and has no such problem.
        · The OS picker is the control the platform's own accessibility layer is built around
          — VoiceOver's rotor and TalkBack's list navigation address it natively, and it
          answers the hardware back gesture.

      What the fallback costs is real and is not hidden: below `sm` there is NO filtering. For
      the language field — set once, per account, almost always from a desk — that is the
      right trade. A field where phone-side search genuinely mattered would need a bottom
      sheet, which is a different component, not a prop on this one.

      Option values are bound as the option's INDEX, not its value. `<option value>` is a DOM
      string, so binding a boolean or a numeric id through it would hand the parent `"true"`
      or `"12"` where it had `true` or `12` — and the v-model contract of this component is
      that the value comes back exactly as it went in.
    -->
    <select
      v-if="useNativeControl"
      :id="id || null"
      ref="native"
      class="w-full mb-0"
      :value="selectedIndex === -1 ? '' : String(selectedIndex)"
      :disabled="disabled"
      :aria-label="label || null"
      @change="onNativeChange"
    >
      <option v-if="selectedIndex === -1" value="" disabled>
        {{ triggerText }}
      </option>
      <option
        v-for="(option, index) in options"
        :key="`${index}-${String(option.value)}`"
        :value="String(index)"
        :disabled="option.disabled"
      >
        {{ option.label }}
      </option>
    </select>

    <template v-else>
      <button
        :id="id || null"
        ref="trigger"
        type="button"
        :disabled="disabled"
        :aria-expanded="isOpen ? 'true' : 'false'"
        aria-haspopup="listbox"
        :aria-controls="isOpen ? listboxId : null"
        :aria-label="triggerAriaLabel"
        class="flex items-center justify-between w-full gap-2 transition-colors duration-150 border border-solid rounded-lg outline-none woot-select__trigger bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-start focus-visible:ring-2 focus-visible:ring-accent-mint/70"
        :class="[triggerSizeClass, triggerStateClass]"
        @click="toggle"
        @keydown.down.prevent="openAt('first')"
        @keydown.up.prevent="openAt('last')"
      >
        <!--
          `title` and not a tooltip component: a label long enough to truncate is the one case
          where the browser's own affordance is both sufficient and free, and it works while
          the popup is shut.
        -->
        <span
          class="min-w-0 truncate"
          :class="hasSelection ? '' : 'text-slate-500 dark:text-slate-400'"
          :title="triggerText"
        >
          {{ triggerText }}
        </span>
        <fluent-icon
          icon="chevron-down"
          size="14"
          aria-hidden="true"
          class="flex-shrink-0 transition-transform duration-200 ease-out text-slate-500 dark:text-slate-400"
          :class="isOpen ? '-rotate-180' : ''"
        />
      </button>

      <transition name="woot-select-pop">
        <div
          v-if="isOpen"
          class="absolute z-50 flex flex-col w-full py-1 overflow-hidden start-0 surface-overlay rounded-lg"
          :class="dropUp ? 'bottom-full mb-1 is-up' : 'top-full mt-1'"
          :style="{ maxHeight: `${popupMaxHeight}px` }"
        >
          <!--
            THE FILTER FIELD. When it is present it — not the listbox — is the focused element,
            because a text field cannot both receive characters and hand keyboard focus to a
            list. That is why it carries `role="combobox"` and the `aria-activedescendant`
            pointer: the focus stays here and the ACTIVE ROW is reported from here, which is
            exactly the arrangement ARIA's combobox pattern describes.
          -->
          <div
            v-if="isSearchable"
            class="flex items-center gap-2 px-3 pb-1.5 mb-1 border-b border-solid border-slate-50 dark:border-white/[0.06]"
          >
            <fluent-icon
              icon="search"
              size="14"
              aria-hidden="true"
              class="flex-shrink-0 text-slate-400 dark:text-slate-400"
            />
            <input
              ref="search"
              v-model="query"
              type="text"
              role="combobox"
              autocomplete="off"
              spellcheck="false"
              aria-expanded="true"
              :aria-controls="listboxId"
              :aria-activedescendant="activeDescendantId"
              aria-autocomplete="list"
              :aria-label="searchLabel"
              :placeholder="searchPlaceholder"
              class="w-full h-8 p-0 m-0 text-sm bg-transparent rounded-none shadow-none border-0 focus:border-0 focus:shadow-none dark:bg-transparent focus:bg-transparent focus:dark:bg-transparent text-slate-900 dark:text-slate-100"
              @keydown.down.prevent="move(1)"
              @keydown.up.prevent="move(-1)"
              @keydown.home="onEdgeKey($event, 'first')"
              @keydown.end="onEdgeKey($event, 'last')"
              @keydown.enter.prevent="commitActive"
              @keydown.tab="close(false)"
            />
          </div>

          <ul
            :id="listboxId"
            ref="listbox"
            role="listbox"
            :aria-label="label || null"
            :aria-activedescendant="isSearchable ? null : activeDescendantId"
            :tabindex="isSearchable ? null : '-1'"
            class="flex-1 min-h-0 m-0 overflow-y-auto list-none outline-none"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.home.prevent="moveToEdge('first')"
            @keydown.end.prevent="moveToEdge('last')"
            @keydown.enter.prevent="commitActive"
            @keydown.space.prevent="commitActive"
            @keydown.tab="close(false)"
          >
            <li
              v-for="(option, index) in visibleOptions"
              :id="optionId(index)"
              :key="`${index}-${String(option.value)}`"
              role="option"
              :aria-selected="isSelected(option) ? 'true' : 'false'"
              :aria-disabled="option.disabled ? 'true' : null"
              class="flex items-start gap-2 mx-1 transition-colors duration-100 rounded-lg"
              :class="[
                isSmall ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm',
                optionClass(option, index),
              ]"
              @click="pick(option)"
              @mousemove="onHover(index)"
            >
              <!--
                The tick keeps its slot whether or not it is drawn, so the labels line up
                instead of shifting by an icon width as the selection moves.
              -->
              <span
                class="flex items-center justify-center flex-shrink-0 w-4 h-4 mt-0.5"
              >
                <fluent-icon
                  v-if="isSelected(option)"
                  icon="checkmark"
                  size="14"
                  aria-hidden="true"
                />
              </span>
              <span class="min-w-0">
                <!--
                  Wraps rather than truncates. In the trigger a long label has nowhere to go
                  and must be cut; in the list it does have somewhere to go, and cutting it
                  there hides the very thing the reader opened the list to compare.
                -->
                <span class="block break-words">{{ option.label }}</span>
                <span
                  v-if="option.description"
                  class="block mt-0.5 text-xs break-words text-slate-500 dark:text-slate-400"
                >
                  {{ option.description }}
                </span>
              </span>
            </li>
          </ul>

          <!--
            Kept OUT of the <ul>. A listbox may contain options and groups; a paragraph of
            explanatory text inside it is announced as an unlabelled row.
          -->
          <p
            v-if="!visibleOptions.length"
            class="px-3 py-3 m-0 text-sm text-slate-500 dark:text-slate-400"
          >
            {{ emptyText }}
          </p>
        </div>
      </transition>

      <!--
        Filtering changes the list without moving focus, so nothing else would announce that it
        changed. Only rendered while searching — a permanent live region narrates every open.
      -->
      <span v-if="isOpen && isSearchable" role="status" class="sr-only">
        {{ resultCountText }}
      </span>
    </template>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { filterOptions } from './selectSearch';

// Every listbox needs an id for its trigger's `aria-controls`, and every row needs one for
// `aria-activedescendant`. Several of these render on one screen, so the id has to be unique.
let idSeed = 0;
const nextId = () => {
  idSeed += 1;
  return idSeed;
};

// Space to leave between the popup and the edge of whatever would clip it.
const GUTTER = 8;
// Below this the popup is too short to be a list, and it is better to flip it to the other
// side than to render four pixels of scroller.
const MIN_POPUP_HEIGHT = 160;
const DEFAULT_POPUP_HEIGHT = 320;

// Anything that scrolls or clips is what actually bounds an absolutely positioned child —
// `window.innerHeight` is the wrong bound inside a modal, and a modal is where half of these
// controls live (`.modal-container` is `overflow: auto`).
const CLIPS = /(auto|scroll|hidden|overlay)/;
const clippingBounds = element => {
  let node = element && element.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    if (CLIPS.test(style.overflowY) || CLIPS.test(style.overflowX)) {
      const rect = node.getBoundingClientRect();
      return { top: rect.top, bottom: rect.bottom };
    }
    node = node.parentElement;
  }
  return { top: 0, bottom: window.innerHeight };
};

export default {
  name: 'WootSelect',

  mixins: [clickaway],

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    // Whatever the `<select>` it replaces was bound to. It is compared and emitted by
    // identity and never coerced, so a numeric agent id stays a number and `false` stays
    // `false` — which a native `<option value>` cannot promise.
    value: {
      type: [String, Number, Boolean, Object],
      default: null,
    },

    // `[{ value, label, disabled?, description? }]`. `description` renders as a second line
    // in the list and is searched, but never appears in the trigger.
    options: {
      type: Array,
      required: true,
    },

    // The accessible name. Supply it even when a visible <label> sits above the control:
    // this is a button, and a `<label>` cannot name a button the way it names an input.
    label: {
      type: String,
      default: '',
    },

    // Shown when nothing is selected, in the muted tier so it reads as absence rather than
    // as a value. Falls back to a translated "Select…".
    placeholder: {
      type: String,
      default: '',
    },

    // `true` / `false` force it; the default decides from the length of the list, which is
    // the only honest rule — a filter over six rows is furniture, over forty it is the point.
    searchable: {
      type: [Boolean, String],
      default: 'auto',
    },

    searchThreshold: {
      type: Number,
      default: 8,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    // Draws the same red hairline Foundation puts on an invalid input, so a converted field
    // still matches its siblings when vuelidate marks it.
    hasError: {
      type: Boolean,
      default: false,
    },

    // Put on the trigger so an existing `<label for>` keeps pointing at a real control.
    id: {
      type: String,
      default: '',
    },

    // Off only for a control that is already inside a sheet of its own.
    nativeOnMobile: {
      type: Boolean,
      default: true,
    },

    // `small` is the inline size the compact rows in this product already use (h-8/text-xs).
    // Two sizes and no more: a third is a design decision, not a prop.
    size: {
      type: String,
      default: 'normal',
      validator: value => ['normal', 'small'].includes(value),
    },
  },

  data() {
    return {
      isOpen: false,
      query: '',
      // Which row the keyboard is on. Distinct from the SELECTED value: arrowing through a
      // list must not change the field until the user commits.
      activeIndex: 0,
      listboxId: `woot-select-${nextId()}`,
      isNarrowViewport: false,
      dropUp: false,
      popupMaxHeight: DEFAULT_POPUP_HEIGHT,
      mediaQuery: null,
    };
  },

  computed: {
    // `$t()` read in `data()` freezes at the language the component first mounted in, and this
    // product switches language in place. Every translated string is therefore computed.
    searchPlaceholder() {
      return this.$t('COMPONENTS.SELECT.SEARCH_PLACEHOLDER');
    },

    searchLabel() {
      return this.label
        ? this.$t('COMPONENTS.SELECT.SEARCH_LABEL_FOR', { label: this.label })
        : this.$t('COMPONENTS.SELECT.SEARCH_PLACEHOLDER');
    },

    emptyText() {
      return this.options.length
        ? this.$t('COMPONENTS.SELECT.NO_RESULTS')
        : this.$t('COMPONENTS.SELECT.NO_OPTIONS');
    },

    resultCountText() {
      return this.$t('COMPONENTS.SELECT.RESULT_COUNT', {
        count: this.visibleOptions.length,
      });
    },

    useNativeControl() {
      return this.nativeOnMobile && this.isNarrowViewport;
    },

    isSearchable() {
      if (this.searchable === true) return true;
      if (this.searchable === false) return false;
      return this.options.length >= this.searchThreshold;
    },

    selectedOption() {
      // Strict identity. A loose compare here would make `0` and `false` the same option, and
      // both are real values in this codebase (`assignee_id: null`, `greetingEnabled: false`).
      return this.options.find(option => option.value === this.value) || null;
    },

    selectedIndex() {
      return this.options.findIndex(option => option.value === this.value);
    },

    hasSelection() {
      return this.selectedOption !== null;
    },

    // A value that is not in the options — a stale agent id, an inbox that was deleted — shows
    // as EMPTY, exactly as a native `<select>` does, and the value itself is left untouched.
    // Silently re-pointing the field at option zero would save a different record than the one
    // the server holds.
    triggerText() {
      if (this.selectedOption) return String(this.selectedOption.label);
      return this.placeholder || this.$t('COMPONENTS.SELECT.PLACEHOLDER');
    },

    triggerAriaLabel() {
      // `aria-label` REPLACES the element's content, so a label without the current value is
      // how a control ends up announcing less than the `<select>` it replaced.
      if (!this.label) return this.triggerText;
      return `${this.label}: ${this.triggerText}`;
    },

    triggerSizeClass() {
      return this.isSmall ? 'h-8 px-2 text-xs' : 'h-10 px-3 text-sm';
    },

    isSmall() {
      return this.size === 'small';
    },

    triggerStateClass() {
      if (this.disabled) {
        return 'cursor-not-allowed bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-400 border-slate-200 dark:border-slate-600';
      }
      if (this.hasError) {
        return 'cursor-pointer border-red-400 dark:border-red-400';
      }
      return 'cursor-pointer border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500';
    },

    visibleOptions() {
      if (!this.isSearchable) return this.options;
      return filterOptions(this.options, this.query);
    },

    // Absent when there is no row to point at. An `aria-activedescendant` naming an id that is
    // not in the document makes a screen reader announce nothing at all.
    activeDescendantId() {
      if (!this.visibleOptions.length) return null;
      return this.optionId(this.activeIndex);
    },
  },

  watch: {
    // Filtering shortens the list under the cursor. Landing on the first MATCH is what a user
    // typing three letters and pressing Enter expects; leaving the index where it was points
    // at whatever happens to be there now.
    query() {
      this.activeIndex = this.firstEnabledIndex(0, 1);
    },

    options() {
      if (this.activeIndex >= this.visibleOptions.length) {
        this.activeIndex = this.firstEnabledIndex(0, 1);
      }
    },
  },

  // `created`, not `mounted`: `matchMedia` needs no DOM, and deciding the branch after the
  // first render paints the desktop trigger for one frame on every phone.
  created() {
    this.watchViewport();
  },

  beforeDestroy() {
    this.unwatchViewport();
  },

  methods: {
    optionId(index) {
      return `${this.listboxId}-option-${index}`;
    },

    isSelected(option) {
      return option.value === this.value;
    },

    optionClass(option, index) {
      if (option.disabled) {
        return 'cursor-not-allowed text-slate-400 dark:text-slate-500';
      }
      return [
        'cursor-pointer',
        this.isSelected(option)
          ? 'text-accent-mint font-medium'
          : 'text-slate-700 dark:text-slate-200',
        index === this.activeIndex ? 'bg-slate-25 dark:bg-white/[0.06]' : '',
      ];
    },

    // ── VIEWPORT ────────────────────────────────────────────────────────────────────────
    // Matched against Tailwind's own `sm` breakpoint so the branch and the utilities in this
    // file agree about what "a phone" is.
    watchViewport() {
      if (typeof window.matchMedia !== 'function') return;
      this.mediaQuery = window.matchMedia('(max-width: 639px)');
      this.isNarrowViewport = this.mediaQuery.matches;
      // `addListener` is the deprecated form, and is all Safari below 14 has — which is still
      // shipping on phones this product is used from.
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onViewportChange);
      } else if (this.mediaQuery.addListener) {
        this.mediaQuery.addListener(this.onViewportChange);
      }
    },

    unwatchViewport() {
      if (!this.mediaQuery) return;
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.onViewportChange);
      } else if (this.mediaQuery.removeListener) {
        this.mediaQuery.removeListener(this.onViewportChange);
      }
      this.mediaQuery = null;
    },

    onViewportChange(event) {
      this.isNarrowViewport = event.matches;
      // Rotating a phone into the native branch while the popup is up would leave an orphaned
      // overlay with nothing under it.
      if (this.isNarrowViewport) this.isOpen = false;
    },

    onNativeChange(event) {
      const index = Number(event.target.value);
      const option = this.options[index];
      if (!option) return;
      this.emit(option.value);
    },

    // ── PLACEMENT ───────────────────────────────────────────────────────────────────────
    measure() {
      const trigger = this.$refs.trigger;
      if (!trigger || typeof trigger.getBoundingClientRect !== 'function') {
        return;
      }
      const rect = trigger.getBoundingClientRect();
      const bounds = clippingBounds(trigger);
      const below = bounds.bottom - rect.bottom - GUTTER;
      const above = rect.top - bounds.top - GUTTER;

      this.dropUp = below < MIN_POPUP_HEIGHT && above > below;
      const room = Math.floor(this.dropUp ? above : below);
      this.popupMaxHeight = Math.max(
        MIN_POPUP_HEIGHT,
        Math.min(DEFAULT_POPUP_HEIGHT, room)
      );
    },

    // ── OPEN / CLOSE ────────────────────────────────────────────────────────────────────
    toggle() {
      if (this.isOpen) {
        this.close();
        return;
      }
      this.open();
    },

    open() {
      if (this.disabled || this.isOpen) return;
      this.query = '';
      // Open ON the current value, not at the top: arrowing should start from where the user
      // already is. Falls back to the first selectable row when the value is not in the list.
      const current = this.options.findIndex(
        option => option.value === this.value && !option.disabled
      );
      this.activeIndex =
        current === -1 ? this.firstEnabledIndex(0, 1) : current;
      this.isOpen = true;

      this.$nextTick(() => {
        this.measure();
        // The focused element is the filter field when there is one, and the LISTBOX when
        // there is not — never an option, because an option that takes focus cannot be
        // reported by `aria-activedescendant`.
        const target = this.isSearchable
          ? this.$refs.search
          : this.$refs.listbox;
        if (target && target.focus) target.focus();
        this.scrollActiveIntoView();
      });
    },

    // Down from a shut control lands on the first row, Up on the last — the same two gestures
    // a native select answers. Only ever reached while shut: once open, focus has moved into
    // the popup and the trigger sees no more keys.
    openAt(edge) {
      if (this.isOpen) return;
      this.open();
      this.$nextTick(() => this.moveToEdge(edge));
    },

    // `returnFocus` is false when the popup closes because the user went SOMEWHERE ELSE —
    // a click outside, or a Tab. Pulling focus back to the trigger then fights the gesture
    // they just made. It is only right when the popup closes on its own terms: Escape, or a
    // selection.
    close(returnFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.query = '';
      if (!returnFocus) return;
      // Otherwise focus lands on <body> and the next Tab restarts from the top of the page.
      this.$nextTick(() => {
        const trigger = this.$refs.trigger;
        if (trigger && trigger.focus) trigger.focus();
      });
    },

    closeFromOutside() {
      this.close(false);
    },

    // Escape has to stop HERE when the popup is up. Half of these controls sit in a dialog,
    // and `woot-modal` closes on Escape too — so without this, dismissing the list also threw
    // away the half-filled form behind it. The native `<select>` never had that problem
    // because the OS popup swallows the key before the page ever sees it.
    //
    // Conditional rather than a blanket `.stop`: with the popup already shut, Escape belongs
    // to the dialog and closing it is exactly what the user is asking for.
    onEscape(event) {
      if (!this.isOpen) return;
      event.stopPropagation();
      this.close();
    },

    // ── KEYBOARD ────────────────────────────────────────────────────────────────────────
    //
    // Down is the next row and Up the previous one in BOTH directions. The list runs down the
    // screen, not across it, so its visual order is identical in Arabic and English — mapping
    // the vertical keys to text direction is the mistake here, not the fix.
    firstEnabledIndex(from, step) {
      const list = this.visibleOptions;
      if (!list.length) return 0;
      for (let i = 0; i < list.length; i += 1) {
        const index =
          (from + i * step + list.length * list.length) % list.length;
        if (!list[index].disabled) return index;
      }
      // Every row disabled: point at the first one so the list is still describable.
      return 0;
    },

    move(step) {
      const list = this.visibleOptions;
      if (!list.length) return;
      let index = this.activeIndex;
      for (let i = 0; i < list.length; i += 1) {
        // Wrapping is what makes a forty-row list navigable from either end.
        index = (index + step + list.length) % list.length;
        if (!list[index].disabled) {
          this.activeIndex = index;
          this.scrollActiveIntoView();
          return;
        }
      }
    },

    moveToEdge(edge) {
      const list = this.visibleOptions;
      if (!list.length) return;
      this.activeIndex =
        edge === 'first'
          ? this.firstEnabledIndex(0, 1)
          : this.firstEnabledIndex(list.length - 1, -1);
      this.scrollActiveIntoView();
    },

    // In the filter field Home and End are CARET keys and stay caret keys — stealing them
    // from a text input is the kind of cleverness that makes a control unusable. They only
    // reach the list when there is no text for the caret to move through.
    onEdgeKey(event, edge) {
      if (this.query) return;
      event.preventDefault();
      this.moveToEdge(edge);
    },

    onHover(index) {
      if (this.visibleOptions[index] && this.visibleOptions[index].disabled) {
        return;
      }
      this.activeIndex = index;
    },

    commitActive() {
      const option = this.visibleOptions[this.activeIndex];
      if (!option) return;
      this.pick(option);
    },

    pick(option) {
      if (option.disabled) return;
      this.emit(option.value);
      this.close();
    },

    emit(value) {
      // `input` carries v-model; `change` is the parity event, so a call site that had
      // `@change` on the `<select>` keeps working — with the VALUE, not a DOM event, because
      // there is no `event.target.value` to read here.
      this.$emit('input', value);
      this.$emit('change', value);
    },

    scrollActiveIntoView() {
      this.$nextTick(() => {
        const listbox = this.$refs.listbox;
        if (!listbox) return;
        const row = listbox.children[this.activeIndex];
        // jsdom has no layout, so it does not implement this at all.
        if (row && typeof row.scrollIntoView === 'function') {
          row.scrollIntoView({ block: 'nearest' });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// Motion that says "this came from the control", not decoration: a short rise and a fade,
// mirrored when the popup opens upward so it still travels away from its trigger.
.woot-select-pop-enter-active,
.woot-select-pop-leave-active {
  transition:
    opacity 120ms ease-out,
    transform 120ms ease-out;
}

.woot-select-pop-enter,
.woot-select-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

// Opening upward, the same 4px has to travel the other way or the popup arrives from the
// wrong side of its own trigger.
.woot-select-pop-enter.is-up,
.woot-select-pop-leave-to.is-up {
  transform: translateY(4px);
}

@media (prefers-reduced-motion: reduce) {
  .woot-select-pop-enter-active,
  .woot-select-pop-leave-active {
    transition: none;
  }
}
</style>
